/**
 * FeatureDetailView — standalone detail page for a Feature cluster
 * (`#feature/:id`), the 8th of 9 catalogued object kinds to get one.
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 4
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { featureById } from '../state/selectors.js';
import { navigate, navigateToObject, navigateToObjectKind } from '../state/hashRouter.js';
import { KIND_META } from '../state/objectKinds.js';
import { useAppStore } from '../state/store.js';
import { WorkflowTrail } from './WorkflowTrail.js';

export interface FeatureDetailViewProps {
  manifest: AtlasManifest;
  featureId: string;
}

export function FeatureDetailView({ manifest, featureId }: FeatureDetailViewProps): React.JSX.Element {
  const feature = featureById(manifest, featureId);
  if (!feature) {
    return <EmptyState icon="help" title={`Feature ${featureId} not found.`} />;
  }
  const { entityTrail } = useAppStore();

  return (
    <div className="detail-view">
      <ObjectHeader
        object={feature}
        icon={KIND_META.feature.icon}
        iconLabel="Feature"
        iconColor={KIND_META.feature.color}
        badges={[{ label: feature.domainId, type: 'domain' as const }]}
        title={feature.label}
        docRef={<span className="mono">{feature.id}</span>}
        chips={[
          {
            label: feature.source === 'doc-section' ? 'From docs' : 'Inferred',
            tone: feature.source === 'doc-section' ? ('good' as const) : ('caution' as const),
          },
        ]}
        actions={
          <button
            className="btn btn-ghost"
            onClick={() =>
              navigate({ kind: 'domain-map', domainId: feature.domainId, nodeKind: 'feature', nodeId: feature.id })
            }
          >
            <Icon name="hub" size="sm" />
            View in Domain Map
          </button>
        }
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <RelationshipsPanel manifest={manifest} kind="feature" id={feature.id} onNavigate={navigateToObjectKind} />
    </div>
  );
}
